package com.schoolconnect.app.feature.reports

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppBackground
import com.schoolconnect.app.designsystem.AppBorder
import com.schoolconnect.app.designsystem.AppButton
import com.schoolconnect.app.designsystem.AppCard
import com.schoolconnect.app.designsystem.AppDanger
import com.schoolconnect.app.designsystem.AppDialogDateField
import com.schoolconnect.app.designsystem.AppLabeledDropdownField
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppOutlinedButton
import com.schoolconnect.app.designsystem.AppDialogTextField
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppPrimarySoft
import com.schoolconnect.app.designsystem.list.AdminListEmptyState
import com.schoolconnect.app.designsystem.list.AdminListFooter
import com.schoolconnect.app.designsystem.list.AdminListSummaryCardItem
import com.schoolconnect.app.designsystem.list.AdminListSummaryCards
import com.schoolconnect.app.designsystem.list.AdminListTableBody
import com.schoolconnect.app.designsystem.list.AdminListTableCard
import com.schoolconnect.app.designsystem.list.AdminListHeaderColumn
import com.schoolconnect.app.designsystem.list.AdminListHeaderRow
import com.schoolconnect.app.designsystem.list.ListTableRowContainer
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.feature.common.pageFooterText
import com.schoolconnect.app.feature.common.pageWindow
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.health_chart
import ischoolconnect.sharedui.generated.resources.reports
import ischoolconnect.sharedui.generated.resources.stat_users
import ischoolconnect.sharedui.generated.resources.user
import org.jetbrains.compose.resources.painterResource

private val roleOptions = listOf("All Roles", "School Admin", "Management", "Teacher", "Parent")
private val operatorOptions = listOf("Less than", "Greater than", "Equal to", "Between")

@Composable
fun ReportsScreen(
    state: ReportsUiState,
    rows: List<UsageAdoptionRowUi>,
    onEvent: (ReportsUiEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    val totalUsers = rows.sumOf { it.totalUsers }
    val activeUsers = rows.sumOf { it.activeUsers }
    val adoptedUsers = rows.sumOf { it.adoptedUsers }
    val neverLoggedIn = rows.sumOf { it.neverLoggedIn }
    val adoptionRate = if (totalUsers == 0) null else adoptedUsers * 100.0 / totalUsers
    val summaryItems = listOf(
        AdminListSummaryCardItem("Total Users", totalUsers.toString(), "Eligible accounts", painterResource(Res.drawable.stat_users), AppPrimary, AppPrimarySoft, AppMuted),
        AdminListSummaryCardItem("Active Users", activeUsers.toString(), "Logged in during period", painterResource(Res.drawable.health_chart), AppPrimary, AppPrimarySoft, AppMuted),
        AdminListSummaryCardItem("Adoption Rate", adoptionRate?.let { "${it.oneDecimal()}%" } ?: "Not Available", "Meaningful activity", painterResource(Res.drawable.reports), AppPrimary, AppPrimarySoft, AppMuted, valueFontSize = if (adoptionRate == null) 18.sp else 28.sp),
        AdminListSummaryCardItem("Never Logged In", neverLoggedIn.toString(), "Up to report end date", painterResource(Res.drawable.user), AppPrimary, AppPrimarySoft, AppMuted)
    )

    Column(
        modifier = modifier
            .fillMaxWidth()
            .verticalScroll(rememberScrollState())
            .background(AppBackground)
            .padding(bottom = 24.dp),
        verticalArrangement = Arrangement.spacedBy(18.dp)
    ) {
        ReportConfiguration(state, onEvent)

        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text("Overall Usage Summary", color = AppNavy, fontSize = 20.sp, fontWeight = FontWeight.SemiBold)
            Row(modifier = Modifier.fillMaxWidth()) {
                AdminListSummaryCards(items = summaryItems)
            }
        }

        ReportPreviewCard(state, rows, onEvent)
    }
}

@Composable
private fun ReportConfiguration(state: ReportsUiState, onEvent: (ReportsUiEvent) -> Unit) {
    AppCard(modifier = Modifier.fillMaxWidth()) {
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val compact = maxWidth < 900.dp
            Column(
                modifier = Modifier.fillMaxWidth().padding(20.dp),
                verticalArrangement = Arrangement.spacedBy(14.dp)
            ) {
                Text("Report Configuration", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                        Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                            RoleField(state, onEvent, Modifier.weight(1f))
                            DateField("From Date", state.fromDate, { onEvent(ReportsUiEvent.FromDateChanged(it)) }, Modifier.weight(1f))
                        }
                        DateField("To Date", state.toDate, { onEvent(ReportsUiEvent.ToDateChanged(it)) }, Modifier.fillMaxWidth())
                    }
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(20.dp)) {
                        RoleField(state, onEvent, Modifier.weight(1.25f))
                        DateField("From Date", state.fromDate, { onEvent(ReportsUiEvent.FromDateChanged(it)) }, Modifier.weight(1f))
                        DateField("To Date", state.toDate, { onEvent(ReportsUiEvent.ToDateChanged(it)) }, Modifier.weight(1f))
                    }
                }

                HorizontalDivider(color = AppBorder)
                Text("Criteria (Optional)", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                if (compact) {
                    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
                        MetricField(state, onEvent, Modifier.fillMaxWidth())
                        if (state.metric != "None") {
                            Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                                OperatorField(state, onEvent, Modifier.weight(1f))
                                PercentageField("Value", state.firstValue, { onEvent(ReportsUiEvent.FirstValueChanged(it)) }, Modifier.weight(1f))
                            }
                            if (state.operator == "Between") {
                                PercentageField("To", state.secondValue, { onEvent(ReportsUiEvent.SecondValueChanged(it)) }, Modifier.fillMaxWidth())
                            }
                        }
                    }
                } else {
                    Row(horizontalArrangement = Arrangement.spacedBy(14.dp), verticalAlignment = Alignment.Bottom) {
                        MetricField(state, onEvent, Modifier.weight(1f))
                        if (state.metric != "None") {
                            OperatorField(state, onEvent, Modifier.weight(1f))
                            PercentageField("Value", state.firstValue, { onEvent(ReportsUiEvent.FirstValueChanged(it)) }, Modifier.weight(.8f))
                            if (state.operator == "Between") {
                                PercentageField("To", state.secondValue, { onEvent(ReportsUiEvent.SecondValueChanged(it)) }, Modifier.weight(.8f))
                            }
                        }
                        Spacer(Modifier.weight(.25f))
                        ReportActions(onEvent)
                    }
                }

                if (state.validationMessage.isNotBlank()) {
                    Text(state.validationMessage, color = AppDanger, fontSize = 12.sp)
                }
                if (compact) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.End,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        ReportActions(onEvent)
                    }
                }
            }
        }
    }
}

@Composable
private fun ReportActions(onEvent: (ReportsUiEvent) -> Unit) {
    Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
        AppOutlinedButton(onClick = { onEvent(ReportsUiEvent.Reset) }) {
            Text("Reset", fontSize = 14.sp)
        }
        AppButton(onClick = { onEvent(ReportsUiEvent.RunReport) }) {
            Text("Run Report", fontSize = 14.sp)
        }
    }
}

@Composable
private fun RoleField(state: ReportsUiState, onEvent: (ReportsUiEvent) -> Unit, modifier: Modifier) {
    AppLabeledDropdownField(
        label = "Role",
        value = state.role,
        options = roleOptions,
        includeAllOption = false,
        onSelected = { onEvent(ReportsUiEvent.RoleChanged(it)) },
        modifier = modifier
    )
}

@Composable
private fun MetricField(state: ReportsUiState, onEvent: (ReportsUiEvent) -> Unit, modifier: Modifier) {
    AppLabeledDropdownField(
        label = "Metric",
        value = state.metric,
        options = listOf("None", "Adoption Rate"),
        includeAllOption = false,
        onSelected = { onEvent(ReportsUiEvent.MetricChanged(it)) },
        modifier = modifier
    )
}

@Composable
private fun OperatorField(state: ReportsUiState, onEvent: (ReportsUiEvent) -> Unit, modifier: Modifier) {
    AppLabeledDropdownField(
        label = "Operator",
        value = state.operator,
        options = operatorOptions,
        includeAllOption = false,
        onSelected = { onEvent(ReportsUiEvent.OperatorChanged(it)) },
        modifier = modifier
    )
}

@Composable
private fun DateField(label: String, value: String, onChange: (String) -> Unit, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(7.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        AppDialogDateField(value, onChange, Modifier.fillMaxWidth(), placeholder = "DD MMM YYYY", fieldHeight = 40.dp)
    }
}

@Composable
private fun PercentageField(label: String, value: String, onChange: (String) -> Unit, modifier: Modifier) {
    Column(modifier, verticalArrangement = Arrangement.spacedBy(7.dp)) {
        Text(label, color = AppNavy, fontSize = 13.sp, fontWeight = FontWeight.SemiBold)
        AppDialogTextField(
            value = value,
            onValueChange = onChange,
            placeholder = "0–100",
            fieldHeight = 40.dp,
            trailingIcon = { Text("%", color = AppMuted, fontSize = 14.sp) }
        )
    }
}

@Composable
private fun ReportPreviewCard(state: ReportsUiState, rows: List<UsageAdoptionRowUi>, onEvent: (ReportsUiEvent) -> Unit) {
    val displayRows = if (state.showFullReport) {
        val request = PageRequest(state.pageIndex, state.pageSize)
        val window = pageWindow(request.pageIndex, request.pageSize, rows.size)
        rows.drop(window.startIndex - if (rows.isNotEmpty()) 1 else 0).take(request.safePageSize)
    } else rows.take(5)
    val appliedCriteria = if (state.appliedMetric == "None") "No adoption-rate criterion" else {
        val symbol = when (state.appliedOperator) {
            "Greater than" -> ">"
            "Equal to" -> "="
            "Between" -> "between"
            else -> "<"
        }
        if (state.appliedOperator == "Between") "Adoption rate between ${state.appliedFirstValue}% and ${state.appliedSecondValue}%"
        else "Adoption rate $symbol ${state.appliedFirstValue}%"
    }
    val window = pageWindow(state.pageIndex, state.pageSize, rows.size)
    val pageCount = pageCountForTotal(rows.size, state.pageSize)

    AppCard(Modifier.fillMaxWidth()) {
        Column(Modifier.fillMaxWidth().padding(top = 18.dp, bottom = 4.dp), verticalArrangement = Arrangement.spacedBy(0.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(5.dp)) {
                    Text(if (state.showFullReport) "Overall Usage Report" else "Report Preview", color = AppNavy, fontSize = 18.sp, fontWeight = FontWeight.SemiBold)
                    Text("Usage & Adoption Report — Overall Usage", color = AppNavy, fontSize = 14.sp, fontWeight = FontWeight.SemiBold)
                    Text("${state.appliedFromDate} – ${state.appliedToDate}  ·  ${state.appliedRole}  ·  $appliedCriteria", color = AppMuted, fontSize = 12.sp)
                }
                if (state.showFullReport) {
                    AppOutlinedButton(onClick = { onEvent(ReportsUiEvent.ShowPreview) }) { Text("Show Preview") }
                }
            }
            Spacer(Modifier.height(14.dp))

            if (rows.isEmpty()) {
                Box(
                    modifier = Modifier.fillMaxWidth().height(132.dp),
                    contentAlignment = Alignment.Center
                ) {
                    AdminListEmptyState(title = "No matching usage data", subtitle = "Adjust the date range, role or adoption criterion and run the report again.")
                }
            } else {
                UsageTable(
            rows = displayRows,
                    fullReport = state.showFullReport,
                    pageText = pageFooterText(window, "roles"),
                    pageIndex = window.pageIndex,
                    pageCount = pageCount,
                    pageSize = state.pageSize,
                    rowsPerPageExpanded = state.rowsPerPageExpanded,
                    onEvent = onEvent
                )
            }

            if (state.feedbackMessage.isNotBlank()) {
                Text(state.feedbackMessage, color = AppMuted, fontSize = 12.sp, modifier = Modifier.padding(horizontal = 20.dp, vertical = 6.dp))
            }

            Row(
                modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp, vertical = 10.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text("${rows.size} role categories · Sample data", color = AppMuted, fontSize = 12.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    if (!state.showFullReport) {
                        AppOutlinedButton(onClick = { onEvent(ReportsUiEvent.ViewFullReport) }) { Text("View Full Report") }
                    }
                    AppOutlinedButton(onClick = { onEvent(ReportsUiEvent.ExportSelected("Excel")) }) { Text("Export to Excel") }
                    AppButton(onClick = { onEvent(ReportsUiEvent.ExportSelected("PDF")) }) { Text("Download PDF") }
                }
            }
        }
    }
}

@Composable
private fun UsageTable(
    rows: List<UsageAdoptionRowUi>,
    fullReport: Boolean,
    pageText: String,
    pageIndex: Int,
    pageCount: Int,
    pageSize: Int,
    rowsPerPageExpanded: Boolean,
    onEvent: (ReportsUiEvent) -> Unit
) {
    val tableHeight = (66.dp + 52.dp * rows.size + if (fullReport) 56.dp else 44.dp).coerceIn(200.dp, 420.dp)
    AdminListTableCard(
        modifier = Modifier.fillMaxWidth().height(tableHeight),
        minContentWidth = 1020.dp,
        header = {
            AdminListHeaderRow(
                columns = listOf(
                    AdminListHeaderColumn("Role", 1.0f),
                    AdminListHeaderColumn("Total Users", .9f, alignCenter = true),
                    AdminListHeaderColumn("Active Users", .9f, alignCenter = true),
                    AdminListHeaderColumn("Adopted Users", .95f, alignCenter = true),
                    AdminListHeaderColumn("Adoption Rate", 1f, alignCenter = true),
                    AdminListHeaderColumn("Avg. Logins / User", 1.15f, alignCenter = true),
                    AdminListHeaderColumn("Never Logged In", 1f, alignCenter = true)
                )
            )
        },
        body = {
            AdminListTableBody(rows, key = { it.role }) { row ->
                ListTableRowContainer {
                    ReportCell(row.role, 1.0f, centered = false)
                    ReportCell(row.totalUsers.toString(), .9f)
                    ReportCell(row.activeUsers.toString(), .9f)
                    ReportCell(row.adoptedUsers.toString(), .95f)
                    val lowRate = row.adoptionRate != null && row.adoptionRate!! < 80.0
                    ReportCell(row.adoptionRate?.let { "${it.oneDecimal()}%" } ?: "Not Available", 1f, color = if (lowRate) AppDanger else AppNavy)
                    ReportCell(row.averageLoginsPerUser.oneDecimal(), 1.15f)
                    ReportCell(row.neverLoggedIn.toString(), 1f)
                }
            }
        },
        footer = {
            if (fullReport) {
                AdminListFooter(
                    pageText = pageText,
                    pageIndex = pageIndex,
                    pageCount = pageCount,
                    onPageChanged = { onEvent(ReportsUiEvent.PageChanged(it)) },
                    pageSize = pageSize,
                    rowsPerPageExpanded = rowsPerPageExpanded,
                    onRowsPerPageToggle = { onEvent(ReportsUiEvent.ToggleRowsPerPage) },
                    onRowsPerPageDismiss = { onEvent(ReportsUiEvent.DismissRowsPerPage) },
                    onPageSizeChanged = { onEvent(ReportsUiEvent.PageSizeChanged(it)) },
                    height = 56.dp,
                    horizontalPadding = 16.dp
                )
            } else {
                Text("Showing ${rows.size.coerceAtMost(5)} role categories", color = AppMuted, fontSize = 12.sp, modifier = Modifier.padding(horizontal = 18.dp, vertical = 16.dp))
            }
        }
    )
}

@Composable
private fun RowScope.ReportCell(
    value: String,
    weight: Float,
    color: Color = AppNavy,
    centered: Boolean = true
) {
    Text(
        value,
        modifier = Modifier.weight(weight).padding(horizontal = 6.dp),
        color = color,
        fontSize = 14.sp,
        textAlign = if (centered) TextAlign.Center else TextAlign.Start,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis
    )
}

private fun Double.oneDecimal(): String = (kotlin.math.round(this * 10.0) / 10.0).let { if (it % 1.0 == 0.0) it.toInt().toString() else it.toString() }
